import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import PracticeSession from "@/components/practice/PracticeSession";
import { ApiError } from "@/lib/api";
import {
  getNextQuestion,
  submitAttempt,
  type PracticeQuestionForClient,
} from "@/lib/practice-api";
import { getProfile, type UserProfile } from "@/lib/profile-api";

/**
 * apps/web :: pages :: PracticePage
 *
 * The /practice route. Mounted inside <ProtectedRoute><OnboardingGate> so
 * we know the caller is authed AND has a complete profile; we still fetch
 * the profile here because we need `current_focus_state` to drive the
 * question feed.
 *
 * Wiring mirrors DashboardPage:
 *   1. Fetch profile on mount.
 *   2. Derive state from profile.current_focus_state (fallback to the
 *      first target state).
 *   3. Render `<PracticeSession>` with `fetchNext` + `submit` bound to
 *      the real API wrappers.
 *
 * Edge case: if getProfile returns null (OnboardingGate should prevent
 * this but we're defensive) we redirect to /onboarding.
 */

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; profile: UserProfile; state: string }
  | { kind: "no-profile" }
  | { kind: "error"; message: string };

export default function PracticePage() {
  const { t } = useTranslation();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        if (profile === null) {
          setLoad({ kind: "no-profile" });
          return;
        }
        const state = profile.current_focus_state ?? profile.target_states[0];
        if (!state) {
          setLoad({ kind: "no-profile" });
          return;
        }
        setLoad({ kind: "ready", profile, state });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setLoad({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (load.kind === "loading") {
    return (
      <LearnerPortalShell pageTitle={t("practice.title")}>
        <div
          role="status"
          aria-live="polite"
          data-testid="practice-page-loading"
          className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
        >
          {t("practice.pageLoading")}
        </div>
      </LearnerPortalShell>
    );
  }

  if (load.kind === "no-profile") {
    return <Navigate to="/onboarding" replace />;
  }

  if (load.kind === "error") {
    return (
      <LearnerPortalShell pageTitle={t("practice.title")}>
        <div
          role="alert"
          data-testid="practice-page-error"
          className="mx-auto mt-xl max-w-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("practice.pageLoadError", { message: load.message })}
        </div>
      </LearnerPortalShell>
    );
  }

  return (
    <LearnerPortalShell pageTitle={t("practice.title")}>
      <section className="mx-auto max-w-3xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
      <header className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
          {t("practice.eyebrow")}
        </p>
        <h1 className="mt-sm font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("practice.title")}
        </h1>
        <div className="mt-md h-1 w-16 rounded-full bg-brand-accent" aria-hidden="true" />
        <p className="mt-md text-base text-neutral-800">
          {t("practice.subtitle", { state: load.state })}
        </p>
      </header>

      <div className="mt-xl">
        <PracticeSession
          stateCode={load.state}
          fetchNext={fetchNextBound}
          submit={submitAttempt}
        />
      </div>
      </section>
    </LearnerPortalShell>
  );
}

/** Bound fetcher — a top-level arrow so the identity stays stable across renders. */
const fetchNextBound = (
  state: string,
): Promise<PracticeQuestionForClient | null> => getNextQuestion(state);
